import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import {
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  type Theme,
  FormControl,
  type SxProps,
  FormHelperText,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import StepTypeChips from './StepTypeChips';
import useActiveWorkflowDefinitions from './hooks/useActiveWorkflowDefinitions';

type WorkflowDefinitionPickerProps = {
  value: number | '';
  onChange: (definitionNumber: number | '') => void;
  helperText?: string;
  disabled?: boolean;
  enabled?: boolean;
  optional?: boolean;
  sx?: SxProps<Theme>;
  hideHeader?: boolean;
};

export default function WorkflowDefinitionPicker({
  value,
  onChange,
  helperText,
  disabled = false,
  enabled = true,
  optional = false,
  sx,
  hideHeader = false,
}: WorkflowDefinitionPickerProps) {
  const { t } = useLocales();
  const definitions = useActiveWorkflowDefinitions(enabled);

  if (definitions.length === 0) {
    return null;
  }

  const selected = definitions.find((definition) => definition.definitionNumber === value);

  return (
    <Box sx={sx}>
      {!hideHeader && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <AccountTreeRoundedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">
            {t(optional ? 'workflows.workflowOptional' : 'workflows.workflow')}
          </Typography>
        </Stack>
      )}

      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{t('workflows.definitionFieldLabel')}</InputLabel>

        <Select
          label={t('workflows.definitionFieldLabel')}
          value={value === '' ? '' : String(value)}
          onChange={(event) => onChange(event.target.value ? Number(event.target.value) : '')}
        >
          <MenuItem value="">
            <em>{t('workflows.noWorkflow')}</em>
          </MenuItem>

          {definitions.map((definition) => (
            <MenuItem key={definition.definitionNumber} value={String(definition.definitionNumber)}>
              {`${definition.name} (${t('workflows.stepCount', {
                count: definition.stepTypes?.length ?? 0,
              })})`}
            </MenuItem>
          ))}
        </Select>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {selected && (
        <Box sx={{ mt: 1.5 }}>
          <StepTypeChips stepTypes={selected.stepTypes ?? []} showOrder />
        </Box>
      )}
    </Box>
  );
}
